import { UAParser } from 'ua-parser-js';
import { Component, OnDestroy } from '@angular/core';
import { WizardPageComponent } from '../../../../core/wizard/wizard-page-component';
import type { WelcomeWizard } from '../welcome-wizard';
import { Observable, of } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StoreVolume } from '../../../../core/store/volume/store-volume';
import { Space, Store } from '../../../../core/models';
import { SalesSqftDisplayType } from '../../../../core/user-preferences/sales-sqft-display-type.enum';
import { SalesVolumeDisplayType } from '../../../../core/user-preferences/sales-volume-display-type.enum';
import * as _ from 'lodash';
import { HitTrackingService } from '../../../../core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { UserPreferencesService } from 'src/app/core/user-preferences/user-preferences.service';
import { map } from 'rxjs/operators';


@Component({
  standalone: false,
  selector: 'mtn-welcome-wizard-volume-page',
  templateUrl: './welcome-wizard-volume-page.component.html',
  styleUrls: ['./welcome-wizard-volume-page.component.scss']
})
export class WelcomeWizardVolumePageComponent extends WizardPageComponent<WelcomeWizard> implements OnDestroy {

  key = 'volume';

  SalesSqftDisplayModeType = SalesSqftDisplayType;
  SalesVolumeDisplayModeType = SalesVolumeDisplayType;

  sampleVolume = new StoreVolume({
    salesArea: 50000,
    totalArea: 75000,
    total: 300000,
    salesSqftBySalesArea: 6,
    salesSqftByTotalArea: 4,
    date: new Date()
  });

  sampleStore = new Store({
    salesArea: 50000,
    space: new Space({
      area: 75000
    })
  });

  constructor(
    private hitTrackingService: HitTrackingService,
    private userPreferencesService: UserPreferencesService
  ) {
    super();
    this.title = 'Welcome! Let\'s get started.';
    this.stepperText = 'Sales Volume';
    this.backButtonText = 'Back';
    this.closeButtonText = 'Close';
    this.doNotShowAgainCheckbox = 'Don\'t show again';
    this.headerText = 'Sales Volume Preferences';
    this.nextButtonText = 'Next';
  }

  onLoad(): Observable<any> {
    this.initForm();
    return super.onLoad();
  }

  onClose(): Observable<null> {
    if (this.wizard.doNotShowAgainCheckboxChecked) {
      const request = _.cloneDeep(this.wizard.model.userPreferences);
      request.isWelcomeWizardComplete = true;
      this.userPreferencesService.updateOne(request)
        .pipe(map(() => null));
    }
    const formValue = this.form?.getRawValue?.() || {};
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    // 🔹 Track Close
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_SETUP_VOLUME,
      {
        ...meta,
        ...OSmetadata,
        ...formValue
      },
      USER_TRACKING_ACTIONS.CLOSE_SETUP_VOLUME,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.CLOSE_SETUP_VOLUME],
      false
    ).subscribe();
    return super.onClose();
  }

  onDonotShowAgainClick(): any {
    const parser = new UAParser(navigator.userAgent).getResult();
    let deviceType = parser.device.type;
    if (!deviceType) {
      deviceType = 'desktop';
    }
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_SETUP_VOLUME,
      { ...meta, ...OSmetadata },
      USER_TRACKING_ACTIONS.OPT_OUT_VOLUME_PAGE,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.OPT_OUT_VOLUME_PAGE],
      false
    ).subscribe();
  }

  onDonotShowAgainUnchecked(): any {
    const parser = new UAParser(navigator.userAgent).getResult();
    let deviceType = parser.device.type;
    if (!deviceType) {
      deviceType = 'desktop';
    }
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_SETUP_VOLUME,
      { ...meta, ...OSmetadata },
      USER_TRACKING_ACTIONS.OPT_IN_VOLUME_PAGE,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.OPT_IN_VOLUME_PAGE],
      false
    ).subscribe();
  }

  onNext(): Observable<string> {
    const formValue = this.form.getRawValue();

    this.wizard.model.request = _.cloneDeep(this.wizard.model.userPreferences);
    this.wizard.model.request.salesVolumeDisplayMode = formValue.salesVolumeDisplayMode;
    this.wizard.model.request.salesSqftDisplayMode = formValue.salesSqftDisplayMode;

    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    // 🔹 Track Next
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_SETUP_VOLUME,
      {
        ...meta,
        ...OSmetadata,
        ...formValue,
        selectedPreferences: {
          salesVolumeDisplayMode: formValue.salesVolumeDisplayMode,
          salesSqftDisplayMode: formValue.salesSqftDisplayMode
        }
      },
      USER_TRACKING_ACTIONS.NEXT,
      USER_TRACKING_ACTION_DESCRIPTIONS.USER_TRACKING_ACTIONS_NEXT_VOLUME,
      false
    ).subscribe();

    return of('key-indicator');
  }

  ngOnDestroy(): void {
    if (!this.wizard.model.userPreferences?.isWelcomeWizardComplete) {

    }
  }

  private initForm(): void {
    this.form = new UntypedFormGroup({
      salesSqftDisplayMode: new UntypedFormControl(this.wizard.model.userPreferences.salesSqftDisplayMode, [Validators.required]),
      salesVolumeDisplayMode: new UntypedFormControl(this.wizard.model.userPreferences.salesVolumeDisplayMode, [Validators.required])
    });
  }


  private buildTrackingMeta(): any {
    return {
      time: new Date().toISOString()
    };
  }
}
